import axios from 'axios';

const API = axios.create({
  baseURL: '',
});

// ── Auth token management ──

const TOKEN_KEY = 'argon-kb-token';

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Auto-inject token into requests
API.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ── Auth API ──

export const sendCode = (phone) =>
  API.post('/auth/send-code', { phone });

export const register = (phone, code, inviteCode) =>
  API.post('/auth/register', { phone, code, invite_code: inviteCode || null });

export const login = (phone, code) =>
  API.post('/auth/login', { phone, code });

export const joinKb = (phone, code, inviteCode) =>
  API.post('/auth/join', { phone, code, invite_code: inviteCode });

export const getMe = () =>
  API.get('/auth/me');

export const getMyInviteCode = () =>
  API.get('/auth/my-invite-code');

// ── File API ──

export const uploadFiles = (markdownFile, originalFile) => {
  const form = new FormData();
  if (markdownFile) form.append('markdown_file', markdownFile);
  form.append('original_file', originalFile);
  return API.post('/files/upload', form);
};

export const uploadBatch = (zipFile) => {
  const form = new FormData();
  form.append('zip_file', zipFile);
  return API.post('/files/upload-batch', form);
};

export const listFiles = () => API.get('/files/list');

export const getServeUrl = (baseName) => {
  const token = getAuthToken();
  const sep = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API.defaults.baseURL}/files/serve/${encodeURIComponent(baseName)}${sep}`;
};

export const downloadFile = async (baseName, originalFilename) => {
  const token = getAuthToken();
  const sep = token ? `?token=${encodeURIComponent(token)}` : '';
  const url = `${API.defaults.baseURL}/files/download/${encodeURIComponent(baseName)}${sep}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = originalFilename;
  a.click();
};

export const deleteFile = (baseName) =>
  API.delete(`/files/${encodeURIComponent(baseName)}`);

// ── Chat API ──

export const askQuestion = (question) =>
  API.post('/chat/ask', { question });

/**
 * Stream answer from LLM.
 * @param {string} question
 * @param {Object} callbacks
 * @param {function(string)} callbacks.onToken - called for each token chunk
 * @param {function(string)} callbacks.onStatus - called for status updates
 * @param {function(string)} callbacks.onThinking - called for thinking/reasoning tokens
 * @param {function()}    callbacks.onThinkingDone - called when thinking phase ends
 * @param {function(string[])} callbacks.onDone - called when stream ends
 * @param {function(string)} callbacks.onError - called on error
 */
export async function askQuestionStream(question, callbacks, { model = null, history = null, thinking = null } = {}) {
  const { onToken, onStatus, onThinking, onThinkingDone, onDone, onError } = callbacks;
  try {
    const body = { question };
    if (model) body.model = model;
    if (history) body.history = history;
    if (thinking !== null) body.thinking = thinking;
    const response = await fetch(`${API.defaults.baseURL}/chat/ask-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthToken();
        window.location.reload();
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sources = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);
        if (data === '[DONE]') continue;

        try {
          const event = JSON.parse(data);
          switch (event.type) {
            case 'token':
              if (event.content) onToken?.(event.content);
              break;
            case 'status':
              if (event.content) onStatus?.(event.content);
              break;
            case 'thinking':
              if (event.content) onThinking?.(event.content);
              break;
            case 'thinking_done':
              onThinkingDone?.();
              break;
            case 'done':
              sources = event.sources || [];
              break;
            default:
              break;
          }
        } catch (e) {
          // ignore parse errors for malformed lines
        }
      }
    }

    onDone?.(sources);
  } catch (err) {
    onError?.(err.message || '请求失败');
  }
}
